import React, { useReducer } from "react";
import createUseContext from "constate";
import { Auth0Context } from "./Auth0Context";

export enum Notification {
  Clear,
  ResetEmailSent,
  LoginFailed,
  SignUpFailed,
  InvalidFormSubmitted,
  LoginFailedCallbackError,
  ResetEmailFailed,
  UnificationEmailSent,
  UnificationEmailFailed
}

enum NotificationActionType {
  ClearNotification,
  ResetEmailSent,
  LoginFailed,
  SignUpFailed,
  InvalidFormSubmitted,
  LoginFailedCallbackError,
  ResetEmailFailed,
  UnificationEmailSent,
  UnificationEmailFailed
}

export type NotificationState = {
  notification: Notification;
  errorCode?: string;
  otherDetails?: any;
};

const NotificaitonInitialState: NotificationState = {
  notification: Notification.Clear
};

type NotificationAction = {
  type: NotificationActionType;
  errorCode?: string;
  otherDetails?: any;
};

function NotificationReducer (
  state: NotificationState,
  action: NotificationAction
): NotificationState {
  switch (action.type) {
    case NotificationActionType.ResetEmailSent:
      return { ...state, notification: Notification.ResetEmailSent, otherDetails: action.otherDetails };
    case NotificationActionType.LoginFailed:
      return {
        ...state,
        notification: Notification.LoginFailed,
        errorCode: action.errorCode,
        otherDetails: action.otherDetails
      };
    case NotificationActionType.SignUpFailed:
      return {
        ...state,
        notification: Notification.SignUpFailed,
        errorCode: action.errorCode,
        otherDetails: action.otherDetails
      };
    case NotificationActionType.ResetEmailFailed:
      return {
        ...state,
        notification: Notification.ResetEmailFailed,
        errorCode: action.errorCode
      };
    case NotificationActionType.InvalidFormSubmitted:
      return { ...state, notification: Notification.InvalidFormSubmitted, otherDetails: action.otherDetails };
    case NotificationActionType.LoginFailedCallbackError:
      return {
        ...state,
        notification: Notification.LoginFailedCallbackError,
        errorCode: action.errorCode,
        otherDetails: action.otherDetails
      };
    case NotificationActionType.UnificationEmailSent:
      return { ...state, notification: Notification.UnificationEmailSent };
    case NotificationActionType.UnificationEmailFailed:
      return {
        ...state,
        notification: Notification.UnificationEmailFailed,
        errorCode: action.errorCode
      };
    default:
      return { ...state, notification: Notification.Clear };
  }
}

const useNotification = () => {
  const [state, dispatch] = useReducer(
    NotificationReducer,
    NotificaitonInitialState
  );

  const resetEmailSent = (auth0Context?: Auth0Context, email?: string) => {
    clearNotification();
    dispatch({ type: NotificationActionType.ResetEmailSent, otherDetails: { auth0Context, email } });
  };

  const clearNotification = () => {
    dispatch({ type: NotificationActionType.ClearNotification });
  };

  const resetEmailFailed = (errorCode?: string) => {
    clearNotification();
    dispatch({ type: NotificationActionType.ResetEmailFailed, errorCode });
  };

  const unificationEmailFailed = (errorCode?: string) => {
    clearNotification();
    dispatch({ type: NotificationActionType.UnificationEmailFailed, errorCode });
  };

  const unificationEmailSent = () => {
    clearNotification();
    dispatch({ type: NotificationActionType.UnificationEmailSent });
  };

  const loginFailed = (errorCode?: string, isFirstLogin?: boolean, switchToResetPassword?: Function, email?: string) => {
    clearNotification();
    dispatch({ type: NotificationActionType.LoginFailed, errorCode, otherDetails: { isFirstLogin, switchToResetPassword, email } });
  };

  const signUpFailed = (errorCode?: string, options?: any, switchedToSignIn?: Function) => {
    clearNotification();
    dispatch({ type: NotificationActionType.SignUpFailed, errorCode, otherDetails: { options, switchedToSignIn, email: options?.email } });
  };

  const invalidFormSubmitted = (pageName?: string) => {
    clearNotification();
    dispatch({ type: NotificationActionType.InvalidFormSubmitted, otherDetails: { pageName } });
  };

  const callbackErrorLoginFailed = (errorCode?: string, options?: any) => {
    clearNotification();
    dispatch({
      type: NotificationActionType.LoginFailedCallbackError,
      errorCode,
      otherDetails: options
    });
  };

  return {
    notification: state,
    resetEmailSent,
    resetEmailFailed,
    clearNotification,
    loginFailed,
    signUpFailed,
    invalidFormSubmitted,
    callbackErrorLoginFailed,
    unificationEmailSent,
    unificationEmailFailed
  };
};

const useNotificationContext = createUseContext(useNotification);
const NotificationContext = useNotificationContext.Context;

interface NotificationProps {
  children: React.ReactNode;
}

function NotificationProvider (props: NotificationProps) {
  const value = useNotification();
  return (
    <NotificationContext.Provider value={value}>
      {props.children}
    </NotificationContext.Provider>
  );
}

export { useNotificationContext, NotificationProvider };
